import { AssetTypes, FamilyRelationshipTypes, FinancialInstitutionTypes } from "@nestoca/multi-tenant";
import * as z from "zod";

// *** NOTE: importing from @shared/utils is not working ***
// *** so we are importing with relative path the functions here ***
// It create a circular dependency
import { getAsNumber } from "../../utils/get-as-number";
import { isEmpty } from "../../utils/is-empty";

import {
  MAX_VALUE,
  RRSP_NON_FTB_RATIO,
  financialTypes,
  giftTypes,
  basicAssetTypes,
} from "./asset.const";
import { CurrencyEnum, VehicleType } from "./asset.enum";
import { isFinancialAsset, isGiftAsset } from "./asset.guards";

import { type DistributiveOmit } from "../../utils";

// https://github.com/nestoca/applications-tmp/blob/daa7069912ca2e93ac779ddc3b9990b71bbf45bd/api/applications_applicant_assets.go
export const withId = z.object({ id: z.coerce.number() });

export const base = z.object({
  id: z
    .preprocess(
      (v) => (isEmpty(v) ? null : getAsNumber(v)),
      z.number({
        error: () => "form:error.required",
      })
    )
    .optional(),
  value: z.preprocess(
    (v) => (isEmpty(v) ? null : getAsNumber(v)),
    z
      .number()
      .max(MAX_VALUE, "form:error.maxDollarAmount")
      .nullable()
      .refine((v): v is number => v != null, { message: "form:error.required" })
  ),
  amountUsedForDownPayment: z.preprocess(
    (v) => (isEmpty(v) ? null : getAsNumber(v)),
    z
      .number({
        error: () => "form:error.required",
        // will coerce nullish values to 0
        // so we can use it as a default value and make it optional
        coerce: true,
      })
      .max(MAX_VALUE, "form:error.customMaxAmount")
  ),
  currency: z.enum(CurrencyEnum, { error: () => "form:error.required" }).default(CurrencyEnum.CAD)

});

// Gift
export const giftOtherRelationshipAsset = z
  .object({
    type: z.enum(giftTypes),
    familyRelationship: z.literal(FamilyRelationshipTypes.OTHER, {
      error: () => "form:error.required",
    }),
    familyRelationshipOther: z
      .string({
        error: () => "form:error.required",
      })
      .trim()
      .min(1, { message: "form:error.required" })
      .max(50, { message: "form:error.tooLong" }),
    donorFirstName: z
      .string({
        error: () => "form:error.required",
      })
      .trim()
      .min(1, { message: "form:error.required" }),
    donorLastName: z
      .string({
        error: () => "form:error.required",
      })
      .trim()
      .min(1, { message: "form:error.required" }),
  })
  .extend(base.shape);

export const giftAsset = giftOtherRelationshipAsset
  .omit({ familyRelationship: true })
  .extend({
    familyRelationship: z.enum(FamilyRelationshipTypes, {
      error: () => "form:error.required",
    }),
    familyRelationshipOther: z.string().trim().optional().nullable(),
  });

// Property
export const propertyAsset = z
  .object({
    type: z.literal(AssetTypes.PROPERTY),
    existingPropertyId: z.preprocess(
      (v) => (isEmpty(v) ? null : getAsNumber(v)),
      z.number({
        error: () => "form:error.required",
      })
    ),
  })
  .extend(base.shape);

// Vehicle
export const vehicleAsset = z
  .object({
    type: z.literal(AssetTypes.VEHICLE),
    make: z
      .string({
        error: () => "form:error.required",
      })
      .trim()
      .min(1, {
        message: "form:error.required",
      })
      .max(64, { message: "form:error.tooLong" })
      .optional(),
    model: z
      .string({
        error: () => "form:error.required",
      })
      .trim()
      .min(1, {
        message: "form:error.required",
      })
      .max(64, { message: "form:error.tooLong" }),
    year: z.coerce
      .number({
        error: () => "form:error.required",
      })
      .min(1800, { message: "form:error.invalidYear" })
      .max(new Date().getFullYear() + 1, { message: "form:error.invalidYear" }),
    vehicleType: z.enum(VehicleType, { error: () => "form:error.required" }).optional().nullable(),
    description: z.string().trim().optional(),
    // this type is an exception because we can't use it as down payment
    // so `value` is shown but not `amountUsedForDownPayment`
    value: z.preprocess(
      (v) => (isEmpty(v) ? null : getAsNumber(v)),
      z
        .number()
        .min(0)
        .max(MAX_VALUE, "form:error.maxDollarAmount")
        .nullable()
        .refine((v): v is number => v != null, { message: "form:error.required" })
    ),
  })
  .extend(base.omit({ value: true }).shape);

const requiredVehicleType = z.preprocess(
  (v) => (v === "" || v == null ? null : v),
  z
    .enum(VehicleType)
    .nullable()
    .refine((v): v is (typeof VehicleType)[keyof typeof VehicleType] => v != null, {
      message: "form:error.required",
    })
);

export const vehicleAssetWithRequiredType = vehicleAsset.extend({
  vehicleType: requiredVehicleType,
});

// Financial
// https://github.com/nestoca/applications-tmp/blob/daa7069912ca2e93ac779ddc3b9990b71bbf45bd/api/applications_applicant_assets.go#L202-L234
// Can't use discriminate union because it doesn't support nested discriminated unions
// so this one will be validated with `refine`
export const financialOtherAsset = z
  .object({
    type: z.enum(financialTypes),
    institution: z.literal(FinancialInstitutionTypes.OTHER, {
      error: () => "form:error.required",
    }),
    institutionOther: z
      .string({
        error: () => "form:error.required",
      })
      .trim()
      .min(1, { message: "form:error.required" })
      .max(50, { message: "form:error.tooLong" }),
  })
  .extend(base.shape);

export const financialOptionalOtherInstitution = financialOtherAsset
  .omit({ institution: true })
  .extend({
    institution: z
      .string({
        error: () => "form:error.required",
      })
      .trim()
      .min(1),
    institutionOther: z.string().optional().nullable(),
  });

const nonOtherInstitutionTypes = Object.values(FinancialInstitutionTypes).filter(
  (v) => v !== FinancialInstitutionTypes.OTHER
) as unknown as [string, ...string[]];

export const financialInstitutionAsset = z
  .object({
    type: z.enum(financialTypes, {
      error: () => "form:error.required",
    }),
    institution: z.enum(nonOtherInstitutionTypes, {
      error: () => "form:error.required",
    }),
  })
  .extend(base.shape);

export const financialAsset = z.union([
  financialOtherAsset,
  financialInstitutionAsset,
]);

// Basic assets (require only type, description, and amount used for down payment)
export const basicAsset = z
  .object({
    type: z.enum(basicAssetTypes),
    description: z.string().trim().optional(),
  })
  .extend(base.shape);

// Other
export const otherAsset = z
  .object({
    type: z.literal(AssetTypes.OTHER),
    name: z
      .string({
        error: () => "form:error.required",
      })
      .trim()
      .min(1, { message: "form:error.required" }),
    description: z.string().trim().optional(),
  })
  .extend(base.shape);

const validationAmountForDownPayment =
  (isFirstTimeHomeBuyer: boolean) =>
  <T extends { type?: string; value?: number; amountUsedForDownPayment?: number | null }>(
    data: T,
    ctx: z.RefinementCtx
  ) => {
    if (data.amountUsedForDownPayment == null || data.value == null) return;
    const maxAmount =
      !isFirstTimeHomeBuyer && data.type === AssetTypes.RRSP
        ? Math.floor(data.value * RRSP_NON_FTB_RATIO)
        : data.value;
    if (data.amountUsedForDownPayment > maxAmount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "form:error.customMaxAmount",
        params: { amount: maxAmount },
        path: ["amountUsedForDownPayment"],
      });
    }
  };

const validationFinancialRefine = <T extends BaseAsset>(data: T) => {
  if (!isFinancialAsset(data)) {
    return true;
  }

  // custom validataion for financial assets
  const res = financialAsset.safeParse(data);
  return res.success;
};

const validationGiftRelationshipRefine = <T extends BaseAsset>(data: T) => {
  if (!isGiftAsset(data) || data.familyRelationship !== FamilyRelationshipTypes.OTHER) {
    return true;
  }

  const res = giftOtherRelationshipAsset.safeParse(data);
  return res.success;
};

const assetTransformer = <T extends BaseAsset>(data: T) => {
  if (data.type === AssetTypes.VEHICLE) {
    return {
      ...data,
      amountUsedForDownPayment: null,
    };
  }

  return data;
};

export const baseAssetSchema = z.discriminatedUnion("type", [
  giftAsset,
  propertyAsset,
  vehicleAsset,
  financialOptionalOtherInstitution,
  basicAsset,
  otherAsset,
]);

const buildBaseSchemaExists = (showVehicleType: boolean) =>
  z.discriminatedUnion("type", [
    giftAsset.extend(withId.shape),
    propertyAsset.extend(withId.shape),
    (showVehicleType ? vehicleAssetWithRequiredType : vehicleAsset).extend(withId.shape),
    financialOptionalOtherInstitution.extend(withId.shape),
    basicAsset.extend(withId.shape),
    otherAsset.extend(withId.shape),
  ]);

const buildSchemaAsset = (isFirstTimeHomeBuyer: boolean, showVehicleType = false) =>
  buildBaseSchemaExists(showVehicleType)
    .refine(validationFinancialRefine, {
      message: "institutionOther is required when institution is OTHER",
      path: ["institutionOther"],
    })
    .refine(validationGiftRelationshipRefine, {
      message: "form:error.required",
      path: ["familyRelationshipOther"],
    })
    .transform(assetTransformer)
    .superRefine(validationAmountForDownPayment(isFirstTimeHomeBuyer));

export const createSchemaAsset = buildSchemaAsset;

// Default to true (first-time home buyer) for backward-compatibility
export const schemaAsset = buildSchemaAsset(true);

const buildBaseSchemaAssetCreate = (showVehicleType: boolean) =>
  z.discriminatedUnion("type", [
    giftAsset,
    propertyAsset,
    showVehicleType ? vehicleAssetWithRequiredType : vehicleAsset,
    financialOptionalOtherInstitution,
    basicAsset,
    otherAsset,
  ]);

const buildSchemaAssetCreate = (isFirstTimeHomeBuyer: boolean, showVehicleType = false) =>
  buildBaseSchemaAssetCreate(showVehicleType)
    .refine(validationFinancialRefine, {
      message: "institutionOther is required when institution is OTHER",
      path: ["institutionOther"],
    })
    .refine(validationGiftRelationshipRefine, {
      message: "form:error.required",
      path: ["familyRelationshipOther"],
    })
    .transform(assetTransformer)
    .superRefine(validationAmountForDownPayment(isFirstTimeHomeBuyer));

export const createNewSchemaAsset = buildSchemaAssetCreate;

// Default to true (first-time home buyer) for backward-compatibility
export const schemaAssetCreate = buildSchemaAssetCreate(true);

// Types
// Cannot extract these types to another file because it will create a circular dependency
// since we are using the utils functions `z.infer` to create the types

export type BaseAsset = z.infer<typeof baseAssetSchema>;
export type ExistingAsset = z.infer<typeof schemaAsset>;

export type Asset = z.infer<typeof schemaAsset>;

export type CreateAsset = z.infer<typeof schemaAssetCreate>;

// **IMPORTANT**
// this is used for the query selector to know which applicant is assigned to the existing asset
export type AssetWithApplicant = Asset & {
  applicant: {
    applicantId: number;
    firstName: string;
    lastName: string;
  };
};

// Type guards utilities
export type GiftAsset = z.infer<typeof giftAsset> & z.infer<typeof withId>;
export type PropertyAsset = z.infer<typeof propertyAsset> &
  z.infer<typeof withId>;
export type VehicleAsset = z.infer<typeof vehicleAsset> &
  z.infer<typeof withId>;
export type FinancialOtherAsset = z.infer<typeof financialOtherAsset> &
  z.infer<typeof withId>;
export type FinancialInstitutionAsset = z.infer<
  typeof financialInstitutionAsset
> &
  z.infer<typeof withId>;
export type FinancialAsset = z.infer<typeof financialAsset> &
  z.infer<typeof withId>;
export type BasicAsset = z.infer<typeof basicAsset> & z.infer<typeof withId>;
export type OtherAsset = z.infer<typeof otherAsset> & z.infer<typeof withId>;

// AssetResponse is the response from<>to the BE
export type AssetResponse = DistributiveOmit<Asset, "applicantId">;
